import React, { useMemo } from "react";
import { ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../contexts/I18nContext";
import { useComposition } from "../contexts/CompositionContext";
import { useCart } from "../contexts/CartContext";
import { captureCurrentPreview } from "../lib/captureCurrentPreview";
import {
  computeSlugPricing,
  formatCHF,
  PRICED_CROSS_SLUGS,
  PRICED_LETTER_SLUGS,
} from "../lib/pricing";
import { ALL_CROSS_DESIGNS } from "./CrossConfigurator";

const SLUG_LABELS = {
  "croix-classique-led":         { fr: "Croix classique LED",     en: "Classic LED cross" },
  "croix-point-a-point":         { fr: "Croix point à point",     en: "Dot-matrix cross" },
  "croix-neon-led":              { fr: "Croix néon LED",          en: "Neon LED cross" },
  "lettre-boitier-alu":          { fr: "Lettres boîtier alu",     en: "Boxed aluminum letters" },
  "lettre-retro-eclaire":        { fr: "Lettres rétro-éclairées", en: "Back-lit letters" },
  "lettre-inox-3d":              { fr: "Lettres inox 3D",         en: "3D stainless letters" },
  "enseigne-boitier-chassis":    { fr: "Enseigne châssis",        en: "Chassis sign" },
  "enseigne-interieur-neon-led": { fr: "Néon intérieur",          en: "Indoor neon" },
  "caducee":                     { fr: "Caducée pharmacie",       en: "Pharmacy caduceus" },
};

/**
 * GlobalPricePanel — affiche tous les éléments configurés sur toutes les
 * pages avec le détail de prix + bouton "Ajouter au panier" par ligne.
 *
 * - Pour le slug courant : utilise `localCfg` (état local en cours d'édition,
 *   pas encore propagé au contexte) pour un affichage temps réel sans lag.
 * - Pour les autres slugs : lit depuis CompositionContext.configs.
 */
export default function GlobalPricePanel({ currentSlug, localCfg }) {
  const { lang } = useI18n();
  const { configs } = useComposition();
  const { addItem: addToCart } = useCart();

  const designsById = useMemo(() => {
    const map = {};
    for (const d of ALL_CROSS_DESIGNS) map[d.id] = d;
    return map;
  }, []);

  const slugList = useMemo(
    () => [...Object.keys(PRICED_CROSS_SLUGS), ...PRICED_LETTER_SLUGS, "caducee"],
    []
  );

  const sections = useMemo(() => {
    return slugList
      .map((slug) => {
        const cfg = slug === currentSlug ? (localCfg || configs[slug]) : configs[slug];
        const pricing = computeSlugPricing({ slug, cfg, designsById });
        return { slug, ...pricing };
      })
      .filter((s) => s.totalCHF > 0 && s.lines.length > 0);
  }, [slugList, currentSlug, localCfg, configs, designsById]);

  const grandTotal = sections.reduce((s, sec) => s + sec.totalCHF, 0);

  /**
   * Ajoute une ligne du montage au panier en embarquant la config complète
   * source (items pour croix, lines pour lettres, plus scène/mode/dimensions).
   * Cela garantit que la fiche de production PDF aura tous les détails
   * techniques nécessaires à la fabrication.
   */
  const handleAddOne = async (slug, kind, line) => {
    const label = line.label;
    // Pour la page courante, on utilise localCfg (zéro lag) sinon le contexte
    const sourceCfg = slug === currentSlug ? (localCfg || configs[slug]) : configs[slug];
    // Filtre items pour ne garder que celui correspondant à la ligne ajoutée
    // (le panneau affiche 1 ligne par item de la composition croix).
    let scopedCfg = { ...(sourceCfg || {}) };
    if (kind === "cross" && line.itemId && Array.isArray(scopedCfg.items)) {
      const matched = scopedCfg.items.find((it) => it.id === line.itemId);
      if (matched) {
        scopedCfg = { ...scopedCfg, items: [matched] };
      }
    }
    let previewImage = null;
    try {
      previewImage = await captureCurrentPreview({ kind });
    } catch { /* fail silently — item.image fallback */ }
    addToCart({
      product_slug: slug,
      product_name: { fr: label, en: label, de: label, it: label },
      image: null,
      configuration: {
        ...scopedCfg,
        kind,
        sourceSlug: slug,
        lineId: line.lineId || line.itemId,
        previewImage,
      },
      unit_price_chf: line.priceCHF,
    });
    toast.success(
      lang === "fr"
        ? `Ajouté au panier — ${formatCHF(line.priceCHF)}`
        : `Added to cart — ${formatCHF(line.priceCHF)}`
    );
  };

  return (
    <div data-testid="price-panel" className="border border-[#39FF14]/40 bg-[#39FF14]/5">
      <div className="flex items-center gap-3 flex-wrap px-4 py-3 border-b border-[#39FF14]/20">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
          {lang === "fr" ? "Prix total du montage" : "Montage total"}
        </span>
        <span
          data-testid="price-total"
          className="font-display text-2xl sm:text-3xl font-semibold text-[#39FF14] tracking-tight"
        >
          {formatCHF(grandTotal)}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          {lang === "fr" ? "HT · Hors pose · TVA en sus" : "Excl. VAT · Excl. installation"}
        </span>
      </div>

      {sections.length === 0 ? (
        <div className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          {lang === "fr"
            ? "Sélectionnez un modèle ou saisissez votre texte pour voir le prix"
            : "Pick a design or enter your text to see pricing"}
        </div>
      ) : (
        <div className="divide-y divide-white/10">
          {sections.map((sec) => {
            const slugLabel =
              (SLUG_LABELS[sec.slug] || {})[lang] ||
              (SLUG_LABELS[sec.slug] || {}).fr ||
              sec.slug;
            const isCurrent = sec.slug === currentSlug;
            return (
              <div key={sec.slug} data-testid={`price-section-${sec.slug}`}>
                <div className="flex items-center justify-between gap-3 px-4 py-2 bg-black/30">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">
                    {slugLabel}
                    {isCurrent && (
                      <span className="ml-2 text-[#39FF14]">
                        {lang === "fr" ? "· page en cours" : "· current page"}
                      </span>
                    )}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#39FF14] tabular-nums">
                    {formatCHF(sec.totalCHF)}
                  </span>
                </div>
                <div className="divide-y divide-white/5">
                  {sec.lines.map((l, idx) => (
                    <div
                      key={l.lineId || l.itemId || idx}
                      data-testid={`price-line-${sec.slug}-${idx}`}
                      className="flex items-center justify-between gap-3 px-4 py-2.5 flex-wrap"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-[11px] text-zinc-200 truncate">{l.label}</div>
                        {l.letters > 0 && (
                          <div className="font-mono text-[9px] text-zinc-500 mt-0.5">
                            {l.letters} × {formatCHF(l.unitPrice)} /{" "}
                            {lang === "fr" ? "lettre" : "letter"}
                          </div>
                        )}
                      </div>
                      <span className="font-display text-base font-semibold text-[#39FF14] shrink-0 tabular-nums">
                        {formatCHF(l.priceCHF)}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleAddOne(sec.slug, sec.kind, l)}
                        data-testid={`add-line-to-cart-${sec.slug}-${idx}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10 hover:bg-[#39FF14]/20 font-mono text-[10px] uppercase tracking-widest shrink-0"
                      >
                        <ShoppingCart size={12} />
                        {lang === "fr" ? "Ajouter au panier" : "Add to cart"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
